import { Outlet, createRootRoute, redirect, useMatches } from '@tanstack/react-router';
import { AppShell } from '@/app/AppShell';
import { ErrorBoundary } from '@/app/ErrorBoundary';
import { reportError } from '@/app/telemetry';
import { isSignedIn } from '@/app/auth';
import type { RouteMeta } from '@/app/route-meta';

// Paths that bypass the auth guard: the sign-in page itself, the
// invitation accept flow (the prospect doesn't have a JWT until accept
// completes), and the auth-mode scaffold from ADR 0017.
const AUTH_FREE_PREFIXES = ['/auth', '/invite'];

export const Route = createRootRoute({
  // Guard every route at the root so a fresh tab (or a signed-out
  // session) bounces to /auth instead of rendering the AppShell
  // against unauthenticated /v1/users/me calls (which would 401 and
  // loop). The redirect carries the original target so /auth can send
  // the user back after a successful sign-in.
  beforeLoad: ({ location }) => {
    if (isSignedIn()) return;
    if (AUTH_FREE_PREFIXES.some((p) => location.pathname.startsWith(p))) return;
    throw redirect({
      to: '/auth',
      search: { redirect: location.pathname + (location.searchStr ?? '') },
    });
  },
  component: RootLayout,
});

function RootLayout() {
  const matches = useMatches();
  // Any match in the active chain can opt out of the shell via
  // staticData: ROUTE_META.fullscreen. Today: only /auth uses this.
  // Phase 4 (3..N) S5 Signal Analysis fullscreen mode reuses the same
  // mechanism.
  const fullscreen = matches.some(
    (m) => (m.staticData as Partial<RouteMeta> | undefined)?.shell === 'none',
  );

  return fullscreen ? (
    <ErrorBoundary onError={reportError}>
      <Outlet />
    </ErrorBoundary>
  ) : (
    <AppShell />
  );
}
